// ========= IMPORTS ==========

import { useNavigate, useLocation } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import { validateQueryParams } from "../utils/validateQueryParams";
import { useContext, useRef } from "react"
import { languageContext } from "../context/LanguageProvider"
import { defaultTexts, updateTexts as updateTextsUtil } from '../utils/surveyUtils';
import { useQuery } from "@tanstack/react-query";
import Loading from "./Loading";
import api from "../api/Api";
import { enterFullscreen } from "../utils/fullscreenAndNavigate";
import { useEffect } from "react";

// ========= HOOKS ==========

function useData(surveyId, userId, enabled) {
  return useQuery({
    queryKey: ['surveyData', surveyId],
    queryFn: async () => {
      const [survey, lan, user] = await Promise.all([
        api.fetchSurveyData(surveyId),
        api.fetchLanguages(surveyId),
        api.fetchRoster(surveyId, userId),
      ]);
      return { survey, lan, user };
    },
    enabled,
  });
}

// ========= COMPONENT DEFINITION ==========

const WelcomeScreen = () => {

  // Debug log: fires each time the WelcomeScreen renders
  console.log("------------------welcomescreen rendered----------------------");

  const { search } = useLocation();
  const query = new URLSearchParams(search);

  const surveyId = query.get("surveyId");
  const panelistId = query.get("panelistId");
  const lang = query.get("lang");
  const hasQueryParams = Boolean(surveyId && panelistId && lang);

  const {changeLanguage} = useContext(languageContext)

  // The navigate function allows redirection to a new route when events occur
  const navigate = useNavigate();

  // Destructures `welcomeText` and `nextButtonText` from the global common context
  // `.current` will contain the actual string/HTML we want to render
  // Destructure shared context values and UI text references
  const { setSurveyId, setUserId, setMemories, setSampleMode,
          intermissionText, implicitExplicitText, 
          consumptionText, surveyEndText, 
          kioskTimeoutText, inputUserIDText, continueButtonText, 
          beginButtonText, intermissionNumber, intermissionTime, welcomeText, nextButtonText, homeTest  } = useCommon();

  // Reference to store language-specific text fetched from API
  const languagesRef = useRef([]);

  var result={};
  const texts = {
    ...defaultTexts
  };


  if(hasQueryParams){
    // --- Validate synchronously before rendering ---
    result = validateQueryParams({ surveyId, panelistId, lang });
  
    // Log depending on validity
    if (!result.valid) {
      console.error("Invalid query parameters:", result.errors);
    } else {
      homeTest.current = true;
      console.log("Validated parameters:", {
        surveyId: result.surveyId,
        panelistId: result.panelistId,
        lang: result.lang,
      });

    }
  }else{
    result.valid = true; // Default to valid if no query params
  }
  
  const { data, isLoading, error } = useData(result.surveyId, result.panelistId, hasQueryParams);

  useEffect(() => {
    if (data && !isLoading && !error && hasQueryParams) {
      setSampleMode(data.survey.sample_mode);
      setSurveyId(result.surveyId);
      setUserId(result.panelistId);

      if (data.user.user_id === result.panelistId) {
        const memoriesArray = data.user.memories.split(',');
        setMemories(memoriesArray);
      }

      intermissionNumber.current = data.lan.intermission_number_aromas;
      intermissionTime.current = data.lan.intermission_time;
      languagesRef.current = data.lan;

      updateTextsUtil({
        languagesRef,
        texts,
        welcomeText,
        implicitExplicitText,
        intermissionText,
        consumptionText,
        surveyEndText,
        beginButtonText,
        continueButtonText,
        inputUserIDText,
        kioskTimeoutText,
        nextButtonText,
      });
      
      changeLanguage(result.lang);
    }
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  if (hasQueryParams) {
    if (error || data?.user?.exists) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
          <div className="text-2xl text-red-600 mb-6 text-center font-normal max-w-full">
            Something went wrong
          </div>
        </div>
      );
    }
  }
  


  // ========= EVENT HANDLERS ==========

  // Called when the "Next" button is clicked
  // goes into full screen mode and then navigates to General Instructions
  const handleNextClick = () => {
    enterFullscreen(navigate, "/GeneralInstructions");
  };

  // ========= RENDER =========
  if(!result.valid){
    return(
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="text-2xl text-red-600 mb-6 text-center font-normal max-w-full">
          Please check the URL and try again.
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      
      {/* Main welcome text area
          - `dangerouslySetInnerHTML` allows rendering HTML directly from the `welcomeText`
            (Careful: Only use with trusted content to avoid XSS vulnerabilities!)
          - Styling: Tailwind classes center it, set font size, color, spacing
      */}
      <div
        className="text-4xl text-gray-800 mb-6 text-center font-normal max-w-full"
        dangerouslySetInnerHTML={{ __html: welcomeText.current }}
      />

      {/* "Next" button
          - Click triggers handleNextClick() → navigates to "/GeneralInstructions"
          - Tailwind styling for padding, colors, hover effect, rounded corners, etc.
      */}
      <button
        onClick={handleNextClick}
        className="px-6 py-2 bg-black text-white text-xl font-semibold rounded-lg shadow hover:bg-gray-800 transition duration-200 mt-10"
      >
        {/* The button label text pulled from context */}
        {nextButtonText.current}
      </button>
    </div>
  );
};

// Export so WelcomeScreen can be used in App routes
export default WelcomeScreen;
